import { debugEnabledByChannel } from '../ui/channel';
import { readPref } from '../ui/prefs';
import { URL_OVERRIDE_KEY, effectiveUrl, workerBase } from './sender';

/**
 * Client des codes de déblocage (A112) : `POST {base}/v1/unlock/redeem`.
 *
 * Un code maison est à **usage unique**, vérifié par le Worker Cloudflare, qui
 * renvoie un jeton gardé sur l'appareil (non revérifié : A112-4). Même
 * discipline que les autres clients (A79) : délai de 8 s, aucune exception
 * vers le jeu, `fetch` injectable pour les tests.
 *
 * Protocole : `docs/contracts/CONTRAT_A112_WORKER.md` §2.
 */

/** Alphabet des codes : 31 symboles, sans 0 / O / 1 / I / L (même règle que le Worker). */
export const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const CODE_LENGTH = 12;
/** Délai d'une requête avant abandon. */
export const REDEEM_TIMEOUT_MS = 8000;
export const REDEEM_PATH = '/v1/unlock/redeem';

/**
 * Code saisi → code normalisé (12 symboles de l'alphabet), ou `null`. Majuscules,
 * tout ce qui n'est pas alphanumérique est retiré (tirets, espaces).
 */
export function normalizeCodeInput(raw: string): string | null {
  const code = String(raw ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (code.length !== CODE_LENGTH) return null;
  for (const ch of code) if (!CODE_ALPHABET.includes(ch)) return null;
  return code;
}

/** `XXXXXXXXXXXX` → `XXXX-XXXX-XXXX` (le reste est rendu tel quel). */
export function formatCode(normalized: string): string {
  return normalized.length === CODE_LENGTH ? `${normalized.slice(0, 4)}-${normalized.slice(4, 8)}-${normalized.slice(8)}` : normalized;
}

/**
 * Formatage au fil de la saisie : majuscules, symboles étrangers retirés,
 * tirets posés tous les quatre caractères, 12 symboles au plus.
 */
export function formatCodeInput(raw: string): string {
  const clean = String(raw ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, CODE_LENGTH);
  return clean.replace(/(.{4})(?=.)/g, '$1-');
}

export type RedeemRefusal = 'invalid' | 'used' | 'rate' | 'unavailable' | 'network';

export type RedeemResult = { ok: true; token: string } | { ok: false; reason: RedeemRefusal };

/** Transport injectable (le `fetch` du navigateur par défaut). */
export type RedeemFetch = (
  url: string,
  init: {
    method: 'POST';
    headers: Record<string, string>;
    body: string;
    signal: AbortSignal;
    credentials: 'omit';
  },
) => Promise<{ ok: boolean; status: number; json?: () => Promise<unknown> }>;

export interface RedeemOptions {
  installId: string;
  /** Racine du Worker (`workerBase(...)`), `null` sans adresse : réponse `unavailable`. */
  base: string | null;
  fetchFn?: RedeemFetch;
  timeoutMs?: number;
}

async function readJson(res: { json?: () => Promise<unknown> }): Promise<Record<string, unknown> | null> {
  try {
    const data = res.json ? await res.json() : null;
    return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Envoie un code au Worker. `200 { ok, token }` → `ok` ; `400` → `invalid` (code
 * mal formé ou inconnu) ou `used` ; `429` → `rate` ; `503` (ou pas d'adresse) →
 * `unavailable` ; hors ligne, délai, autre réponse → `network`. Ne lève jamais.
 */
export async function redeemUnlockCode(code: string, opts: RedeemOptions): Promise<RedeemResult> {
  const normalized = normalizeCodeInput(code);
  if (!normalized) return { ok: false, reason: 'invalid' };
  if (!opts.base) return { ok: false, reason: 'unavailable' };
  const fetchFn: RedeemFetch =
    opts.fetchFn ?? ((url, init) => fetch(url, init as RequestInit) as unknown as ReturnType<RedeemFetch>);
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? REDEEM_TIMEOUT_MS);
  // Le délai couvre aussi la lecture du corps : un Worker qui s'arrête en route ne bloque rien.
  const aborted = new Promise<never>((_, reject) => {
    ctl.signal.addEventListener('abort', () => reject(new Error('délai dépassé')));
  });
  aborted.catch(() => undefined);
  try {
    const work = (async (): Promise<RedeemResult> => {
      const res = await fetchFn(`${opts.base}${REDEEM_PATH}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ installId: opts.installId, code: formatCode(normalized) }),
        signal: ctl.signal,
        credentials: 'omit',
      });
      if (res.status === 200) {
        const data = await readJson(res);
        const token = data && data.ok === true && typeof data.token === 'string' ? data.token : '';
        return token ? { ok: true, token } : { ok: false, reason: 'network' };
      }
      if (res.status === 400) {
        const data = await readJson(res);
        return { ok: false, reason: data?.reason === 'used' ? 'used' : 'invalid' };
      }
      if (res.status === 429) return { ok: false, reason: 'rate' };
      if (res.status === 503) return { ok: false, reason: 'unavailable' };
      return { ok: false, reason: 'network' };
    })();
    return await Promise.race([work, aborted]);
  } catch {
    return { ok: false, reason: 'network' };
  } finally {
    clearTimeout(timer);
  }
}

// ───────────────────────────────────────────────────────── Adresse du Worker

/** Variable de build (Cloudflare Pages) : adresse du Worker, racine ou `…/v1/reports`. */
const BUILD_URL = (import.meta.env.VITE_TELEMETRY_URL as string | undefined) ?? undefined;

/**
 * Racine du Worker pour les clients du mur (codes, événements) : la même
 * résolution que la télémétrie (surcharge Debug, sinon variable de build).
 * Lue à chaque appel, `null` sans adresse. Ne lève jamais.
 */
export function currentWorkerBase(): string | null {
  try {
    let debug = false;
    try {
      debug = debugEnabledByChannel();
    } catch {
      debug = false;
    }
    return workerBase(effectiveUrl({ buildUrl: BUILD_URL, override: readPref(URL_OVERRIDE_KEY), debug }));
  } catch {
    return null;
  }
}
